import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table as MuiTable,
  makeStyles,
} from '@material-ui/core';
import Row from './row';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import { TASK_EVIDENCE_TYPE } from 'utils/constants/task';
import { isBase64 } from 'utils/checkImage';
import { convertToUTCTime } from 'utils/time';
import isEmpty from 'lodash/isEmpty';

const useStyles = makeStyles(theme => ({
  table: {
    '& [class*="MuiTableCell-root"]': {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.text.primary}`,
    },
  },
  root: {
    marginBottom: '5px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  rootCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  mySvgStyle: {
    height: '25px', //*[@id="Layer_1"]/g/path
    width: '25px',
    cursor: 'pointer',
    color: `${theme.palette.text.primary} !important`,
    '& [id="Layer_1"]': {
      fill: `${theme.palette.text.primary} !important`,
      '& [class*="st0"]': {
        fill: `${theme.palette.text.primary} !important`,
      },
    },
  },
}));

const setCorrectEvidence = (rowData, vals, id) => {
  switch (rowData.type) {
    case TASK_EVIDENCE_TYPE.OPTION_SINGLE.id: {
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: true,
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };
      const changeOptions = setCompleted.contents.value.options.map(m => {
        if (m.title === vals.id) {
          return {
            ...m,
            selected: true,
          };
        }
        return { ...m, selected: false };
      });
      setCompleted.contents.value.options = changeOptions;
      return setCompleted;
    }
    case TASK_EVIDENCE_TYPE.OPTION_MULTIPLE.id: {
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: !isEmpty(vals),
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };

      const changeOptions = setCompleted.contents.value.options.map(m => {
        if (vals && vals.includes(m.title)) {
          return {
            ...m,
            selected: true,
          };
        }
        return { ...m, selected: false };
      });
      setCompleted.contents.value.options = changeOptions;
      return setCompleted;
    }
    case TASK_EVIDENCE_TYPE.SIGNATURE.id: {
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: !isEmpty(vals),
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };
      if (isBase64(vals)) {
        setCompleted.contents.value = vals;
      }
      return setCompleted;
    }
    case 'DESCRIPTION': {
      // vals is an event
      const text = vals.target.value;
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: !isEmpty(text),
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };
      if (!setCompleted.contents.value) {
        setCompleted.contents.value = text;
      } else setCompleted.contents.value += text;
      return setCompleted;
    }
    case 'NUMERIC': {
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: !isEmpty(vals),
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };
      if (!setCompleted.contents.value) {
        setCompleted.contents.value = vals;
      } else setCompleted.contents.value += vals;
      // setCompleted.contents.value += vals;
      return setCompleted;
    }
    case 'IMAGE': {
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: !isEmpty(vals),
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };
      setCompleted.contents.value = vals && vals[0].url;
      return setCompleted;
    }
    case 'AUDIO': {
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: !isEmpty(vals),
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };
      setCompleted.contents.value = vals && vals[0].url;
      return setCompleted;
    }
    case 'CHECKBOX': {
      const setCompleted = {
        ...rowData,
        id,
        completed: {
          ...rowData.completed,
          value: !isEmpty(vals),
        },
        timestamp: convertToUTCTime({
          date: Date.now(),
        }),
      };
      setCompleted.contents.value = vals;
      return setCompleted;
    }
    default:
      return false;
  }
};

const CollectEvidence = ({
  data,
  columns,
  className,
  startTaskCb,
  endTaskCb,
  disableStartButton,
  disableEndButton,
  setFieldValue,
  value = [],
  field,
  disabled,
  removeStartEndButtons = false,
  setCompleteToFalse,
}) => {
  const { t } = useTranslation();
  const setValue = ({ vals, id }) => {
    //find the required evidence from list and update its value
    const requiredEvidence = data.find(f => f.rawData.id === id);
    const rowdata = requiredEvidence.rowData;
    const setEvidence = setCorrectEvidence(rowdata, vals, id);
    // check if the evidence exists
    let newArr = [];
    if (value.length === 0) {
      newArr = [setEvidence];
    } else {
      newArr = [...value];
      const i = newArr.findIndex(o => o.id === id);
      if (newArr[i]) {
        newArr[i] = setEvidence;
      } else {
        newArr.push(setEvidence);
      }
    }
    // set it back to formik
    typeof setFieldValue === 'function' && setFieldValue(field, [...newArr]);
  };
  const handleDelete = val => {
    if (typeof setCompleteToFalse === 'function') {
      setCompleteToFalse(field, val);
      return;
    }
    const filteredList = value.filter(f => f.id !== val.id);
    //find the required evidence from list and delete its value
    // add it to the existing value array
    // set it back to formik
    typeof setFieldValue === 'function' && setFieldValue(field, filteredList);
  };
  const renderRows = (row, index) => {
    return (
      <Row
        key={index + 1}
        index={index}
        row={row}
        setValue={setValue}
        value={value}
        handleDelete={handleDelete}
        classes={classes}
        t={t}
        disabled={disabled}
      />
    );
  };
  const classes = useStyles();
  return (
    <div className={classes.container}>
      {!removeStartEndButtons && (
        <Button
          text="Start task"
          onClick={startTaskCb}
          disabled={disableStartButton}
        />
      )}
      <TableContainer className={classes.root}>
        <MuiTable
          className={cn(classes.table, className)}
          aria-label="collapsible table"
        >
          <TableHead>
            <TableRow>
              {columns.map(item => {
                return (
                  <TableCell key={item.field || item.headerName}>
                    {t(item.headerName)}
                  </TableCell>
                );
              })}

              <TableCell>{t('Action')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, index) => {
              return renderRows(row, index);
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {!removeStartEndButtons && (
        <Button
          text="End task"
          onClick={endTaskCb}
          disabled={disableEndButton}
        />
      )}
    </div>
  );
};

CollectEvidence.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  className: PropTypes.string,
  startTaskCb: PropTypes.func,
  endTaskCb: PropTypes.func,
  disableStartButton: PropTypes.bool,
  disableEndButton: PropTypes.bool,
  setFieldValue: PropTypes.func,
  value: PropTypes.object,
  field: PropTypes.string,
  disabled: PropTypes.bool,
  removeStartEndButtons: PropTypes.bool,
  setCompleteToFalse: PropTypes.func,
};

export default CollectEvidence;
